/**
 * Bundle 139 P0-3 - Framing sentinel status tile.
 *
 * Renders on `/admin/health` (AdminDataHealthPage). Shows the
 * in-process framing sentinel counter, alert threshold, whether
 * the alerter flag is enabled, and which transports are wired.
 *
 * "Run alert test" button forces a dry-run alert with the current
 * snapshot (bypasses the threshold) so ops can prove the wire is
 * live end-to-end before an incident.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ShieldAlert, Zap } from 'lucide-react';
import api from '@/utils/api';

const FramingSentinelTile = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/_debug/framing-sentinel/alerter-status');
      setStatus(data);
    } catch (e) {
      // Non-fatal
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/admin/_debug/framing-sentinel/alerter-test');
      setTestResult(data);
      if (data?.fired) toast.success('Alert fired (Sentry + PagerDuty payload sent).');
      else if (data?.reason === 'flag_off') toast.warning('Alert would-not-fire: FRAMING_SENTINEL_ALERT_ENABLED is off.');
      else if (data?.reason === 'no_transport_configured') toast.warning('No SENTRY_DSN / PAGERDUTY_ROUTING_KEY configured. Payload logged only.');
      else toast.success('Test complete.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed');
    } finally { setBusy(false); }
  };

  if (!status) return null;

  const {
    violations_total, threshold_per_hour, flag_enabled,
    sentry_configured, pagerduty_configured, by_reason, by_path,
  } = status;

  const healthy = violations_total < threshold_per_hour;

  return (
    <Card data-testid="framing-sentinel-tile">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-secondary-800 text-[11px] uppercase tracking-widest font-medium">
              <ShieldAlert className="h-4 w-4" /> Framing sentinel * Bundle 139
            </div>
            <div className="mt-1 font-display text-lg font-semibold text-primary-900">
              HTTP framing safety net
            </div>
            <p className="text-sm text-primary-700 mt-1 max-w-xl">
              Enforces RFC 7230 3.3.3 on every response. Heals violations in
              place and counts them here. If this number climbs, a middleware
              is producing malformed responses and Cloudflare will 520.
            </p>
          </div>
          <div className={`rounded-lg px-3 py-2 text-sm font-medium border ${healthy ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-accent-100 text-accent-800 border-accent-200'}`}
                    data-testid="framing-sentinel-tile-status">
            {healthy ? 'Healthy' : 'Above threshold'}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          <Metric label="Violations" value={violations_total} testid="framing-sentinel-total" />
          <Metric label="Threshold / hour" value={threshold_per_hour} testid="framing-sentinel-threshold" />
          <Metric label="Alerter" value={flag_enabled ? 'ON' : 'OFF'} testid="framing-sentinel-flag" tone={flag_enabled ? 'ok' : 'muted'} />
          <Metric label="Transport" value={[sentry_configured && 'Sentry', pagerduty_configured && 'PagerDuty'].filter(Boolean).join(' + ') || 'none'} testid="framing-sentinel-transport" />
        </div>

        {Object.keys(by_reason || {}).length ? (
          <div className="mt-4 text-xs text-neutral-600">
            <div className="font-medium text-neutral-800 mb-1">By reason</div>
            {Object.entries(by_reason).map(([k, v]) => (
              <div key={k} data-testid={`framing-sentinel-reason-${k}`} className="flex justify-between">
                <span className="font-mono">{k}</span><span>{v}</span>
              </div>
            ))}
          </div>
        ) : null}

        <div className="mt-4 flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={runTest} disabled={busy}
                      data-testid="framing-sentinel-tile-run-test">
            <Zap className="h-3.5 w-3.5 mr-1" /> {busy ? 'Testing...' : 'Run alert test'}
          </Button>
          <Button size="sm" variant="outline" onClick={load}
                      data-testid="framing-sentinel-tile-refresh">
            Refresh
          </Button>
        </div>

        {testResult ? (
          <pre className="mt-3 text-[11px] bg-neutral-50 border border-neutral-200 rounded p-2 max-h-72 overflow-auto"
                   data-testid="framing-sentinel-tile-test-output">
            {JSON.stringify(testResult, null, 2)}
          </pre>
        ) : null}
      </CardContent>
    </Card>
  );
};

const Metric = ({ label, value, testid, tone }) => (
  <div className="rounded-lg border border-neutral-200 bg-white p-3">
    <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
    <div
      className={`font-display text-2xl font-bold mt-0.5 ${tone === 'muted' ? 'text-neutral-500' : 'text-primary-900'}`}
      data-testid={testid}
    >
      {value}
    </div>
  </div>
);

export default FramingSentinelTile;
